import {useRef, useEffect, useState, ChangeEvent} from 'react'
import {v4 as uuid} from 'uuid'
import {selectTheme} from '../../store'

export interface SlideCheckboxT {
  id?: string
  value: boolean
  onChange: (checked: boolean) => void
  disabledColor?: string
  readOnly?: boolean
  from?: string // for RTL
  /** Accessible name for the toggle (visual label is empty). */
  ariaLabel?: string
}

export const SlideCheckbox = (props: SlideCheckboxT) => {
  const {color} = selectTheme()
  const id = props.id || uuid()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [value, setValue] = useState(props.value || false)
  const [focused, setFocused] = useState(false)
  const [mouseover, setMouseover] = useState(false)
  const disabledColor = props.disabledColor || color.surface.disabled

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.checked)
    if (props.onChange) props.onChange(e.target.checked)
  }

  const getFocus = () => setFocused(true)
  const looseFocus = () => (mouseover ? getFocus() : setFocused(false))
  const getMouse = () => setMouseover(true)
  const looseMouse = () => setMouseover(false)

  useEffect(() => {
    if (value != props.value) {
      setValue(props.value)
    }
  }, [props.value])

  useEffect(() => {
    const input = inputRef.current as HTMLInputElement | null
    const track = trackRef.current
    const cleanUp: (() => void)[] = []
    if (input) {
      input.addEventListener('focus', getFocus)
      input.addEventListener('blur', looseFocus)
      cleanUp.push(() => input.removeEventListener('focus', getFocus))
      cleanUp.push(() => input.removeEventListener('blur', looseFocus))
    }
    if (track) {
      track.addEventListener('mouseover', getMouse)
      track.addEventListener('mouseleave', looseMouse)
      cleanUp.push(() => track.removeEventListener('mouseover', getMouse))
      cleanUp.push(() => track.removeEventListener('mouseleave', looseMouse))
    }
    return () => {
      cleanUp.forEach((cb) => cb())
    }
  }, [focused, mouseover])

  const {ariaLabel} = props

  return (
    <div css={{position: 'relative'}}>
      <input
        data-testid={`${props.from}_slide_checkbox`}
        type={props.readOnly ? 'hide' : 'checkbox'}
        id={props.id || id}
        ref={inputRef}
        onChange={onChange}
        checked={value}
        aria-label={ariaLabel ?? 'Toggle'}
        css={{
          position: 'absolute',
          cursor: 'pointer',
          opacity: 0,
          height: 0,
          width: 0,
        }}
      />
      <div
        ref={trackRef}
        aria-hidden
        onClick={() => {
          if (!props.readOnly && inputRef.current) {
            inputRef.current.click()
          }
        }}
        css={{
          position: 'relative',
          cursor: props.readOnly ? 'default' : 'pointer',
          display: 'block',
          width: '28px',
          height: '16px',
          borderRadius: '1rem',
          background: value ? color.primary : disabledColor,
        }}>
        <div
          css={{
            position: 'absolute',
            width: '12px',
            height: '12px',
            borderRadius: '1rem',
            background: color.white,
            top: '50%',
            transform: `translateY(-50%) translateX(${value ? '.8rem' : '.2rem'})`,
            transition: 'transform 100ms',
          }}
        />
      </div>
    </div>
  )
}
